import { Control, Controller, FieldErrors } from "react-hook-form";
import {
  ExistingPatientContactFormSchema,
  NewPatientContactFormSchema,
} from "./contactFormSchema";
import { AlertCircle } from "lucide-react";
import React from "react";
import {
  ETHNICITY_OPTIONS,
  CANDIAN_INDIGENOUS_OPTIONS,
} from "../../utils/attributeMapping/EthnicityOptions";

const ethnicityOptions = [
  "Hispanic or Latino",
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "Native Hawaiian or Other Pacific Islander",
  "White",
  "Other",
];

interface EthnicitySelectorProps {
  control: Control<
    ExistingPatientContactFormSchema | NewPatientContactFormSchema
  >;
  errors: FieldErrors<NewPatientContactFormSchema>;
}

export function EthnicitySelector({ control, errors }: EthnicitySelectorProps) {
  return (
    <div data-test-id="ethnicity-section">
      <label className="block text-sm font-medium text-gray-700">
        Race & Ethnicity{" "}
        <span className="text-gray-500 font-normal">
          (optional, select up to two)
        </span>
      </label>
      <div className="mt-2 space-y-1">
        {ethnicityOptions.map((ethnicity) => {
          // Get the current value directly for checked state calculation within Controller
          // No need for external watch or state calculation for checked/disabled

          return (
            <Controller
              key={ethnicity}
              name="ethnicity"
              control={control}
              render={({ field }) => {
                const currentEthnicity = field.value || [];
                const isChecked = currentEthnicity.includes(ethnicity);
                const isDisabled = !isChecked && currentEthnicity.length >= 2;

                return (
                  <div
                    className={`relative flex items-start p-2 rounded-lg transition-colors duration-200 group ${
                      isDisabled
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex h-5 items-center">
                      <input
                        id={`ethnicity-${ethnicity.toLowerCase().replace(/\s+/g, "-")}`}
                        data-test-id={`input-ethnicity-${ethnicity.toLowerCase().replace(/\s+/g, "-")}`}
                        type="checkbox"
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={() => {
                          if (isDisabled) return;

                          let newValue;
                          if (isChecked) {
                            newValue = currentEthnicity.filter(
                              (e) => e !== ethnicity,
                            );
                          } else {
                            newValue = [...currentEthnicity, ethnicity];
                          }
                          field.onChange(newValue);
                        }}
                        className={`h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200 ${
                          isDisabled ? "cursor-not-allowed" : ""
                        }`}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label
                        htmlFor={`ethnicity-${ethnicity.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`font-medium text-gray-700 transition-colors duration-200 ${
                          isDisabled
                            ? "cursor-not-allowed"
                            : "group-hover:text-gray-900 cursor-pointer"
                        }`}
                      >
                        {ethnicity}
                      </label>
                    </div>
                  </div>
                );
              }}
            />
          );
        })}
      </div>
      {/* Display validation error message */}
      {errors.ethnicity && (
        <div className="text-red-500 text-sm mt-2 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {errors.ethnicity?.message}
        </div>
      )}
    </div>
  );
}

interface CanadianEthnicitySelectorProps {
  control: Control<
    ExistingPatientContactFormSchema | NewPatientContactFormSchema
  >;
  errors: FieldErrors<NewPatientContactFormSchema>;
}

export function CanadianEthnicitySelector({
  control,
  errors,
}: CanadianEthnicitySelectorProps) {
  return (
    <div data-test-id="canadian-ethnicity-section">
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Indigenous Identity
        </label>
        <div className="mt-1 space-y-1">
          <Controller
            name="ethnicity"
            control={control}
            render={({ field }) => {
              const currentEthnicity = field.value || [];

              return (
                <div className="space-y-1">
                  {CANDIAN_INDIGENOUS_OPTIONS.map((option) => {
                    const isSelected = currentEthnicity.includes(option.label);

                    return (
                      <div
                        key={option.id}
                        className="relative flex items-start p-2 rounded-lg transition-colors duration-200 group hover:bg-gray-50"
                      >
                        <div className="flex h-5 items-center">
                          <input
                            id={option.id}
                            data-test-id={`input-${option.id}`}
                            type="radio"
                            checked={isSelected}
                            onChange={() => {
                              // Remove any previously selected indigenous options
                              const filteredEthnicity = currentEthnicity.filter(
                                (e) =>
                                  !CANDIAN_INDIGENOUS_OPTIONS.some(
                                    (opt) => opt.label === e,
                                  ),
                              );

                              // Add the new selection
                              const newValue = [
                                ...filteredEthnicity,
                                option.label,
                              ];
                              field.onChange(newValue);
                            }}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor={option.id}
                            className="font-medium text-gray-700 transition-colors duration-200 group-hover:text-gray-900 cursor-pointer"
                          >
                            {option.label}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
        </div>
      </div>

      <Controller
        name="ethnicity"
        control={control}
        render={({ field }) => {
          const currentEthnicity = field.value || [];
          const selectedIndigenousOption = CANDIAN_INDIGENOUS_OPTIONS.find(
            (option) => currentEthnicity.includes(option.label),
          );

          // If nothing is selected yet, render empty fragment
          if (!selectedIndigenousOption) {
            return <React.Fragment />;
          }

          const isNonIndigenous =
            selectedIndigenousOption.label ===
            CANDIAN_INDIGENOUS_OPTIONS[0].label;

          // If indigenous (Yes option selected), don't show additional ethnicity options
          if (!isNonIndigenous) {
            return <React.Fragment />;
          }

          // Only show ethnicity options for non-indigenous users
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ethnicity
                <span className="text-gray-500 font-normal ml-1">
                  (select up to two)
                </span>
              </label>
              <div className="mt-2 space-y-2">
                {ETHNICITY_OPTIONS.map((option) => {
                  const isChecked = currentEthnicity.includes(option.label);
                  const isDisabled =
                    !isChecked &&
                    currentEthnicity.filter(
                      (e) =>
                        !CANDIAN_INDIGENOUS_OPTIONS.some(
                          (opt) => opt.label === e,
                        ),
                    ).length >= 2;

                  return (
                    <div
                      key={option.id}
                      className={`relative flex items-start p-2 rounded-lg transition-colors duration-200 group ${
                        isDisabled
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex h-5 items-center">
                        <input
                          id={option.id}
                          data-test-id={`input-${option.id}`}
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => {
                              if (isDisabled) return;

                              let newValue;
                              if (isChecked) {
                                // Remove this ethnicity if already selected
                                newValue = currentEthnicity.filter(
                                  (e) => e !== option.label,
                                );
                              } else {
                                // Add this ethnicity
                                newValue = [...currentEthnicity, option.label];
                              }

                              field.onChange(newValue);
                            }}
                          className={`h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200 ${
                            isDisabled ? "cursor-not-allowed" : ""
                          }`}
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label
                          htmlFor={option.id}
                          className={`font-medium text-gray-700 transition-colors duration-200 ${
                            isDisabled
                              ? "cursor-not-allowed"
                              : "group-hover:text-gray-900 cursor-pointer"
                          }`}
                        >
                          {option.label}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }}
      />

      {/* Display validation error message */}
      {errors.ethnicity && (
        <div className="text-red-500 text-sm mt-2 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {errors.ethnicity?.message}
        </div>
      )}
    </div>
  );
}
